package com.castly.admin.ui.admin

import androidx.compose.runtime.*
import com.castly.admin.api.AdminUserInfoDto
import com.castly.admin.api.ApiClient
import com.castly.admin.api.PermissionEntryDto
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.admin.ui.icons.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val GROUPS = listOf(
    "" to "None",
    "castly-viewer" to "Viewer",
    "castly-label-editor" to "Label Editor",
    "castly-editor" to "Editor",
    "castly-admin" to "Admin",
    "castly-super-admin" to "Super Admin"
)

private val PERMISSION_LEVELS = listOf("NONE", "VIEW", "EDIT", "PUBLISH", "DELETE")

private val VERTICAL_LABELS = mapOf(
    "audiobooks" to "Audiobooks",
    "podcasts" to "Podcasts",
    "authors" to "Authors",
    "readers" to "Readers",
    "tags" to "Tags",
    "home" to "Home",
    "downloads" to "Downloads",
    "import" to "Import",
    "analytics" to "Analytics",
    "audit_log" to "Audit Log",
    "api_docs" to "API Docs",
    "cache" to "Cache"
)

private fun levelColor(level: String): String = when (level) {
    "NONE" -> "#6b7280"
    "VIEW" -> "#3b82f6"
    "EDIT" -> "#f59e0b"
    "PUBLISH" -> "#10b981"
    "DELETE" -> "#ef4444"
    else -> "#6b7280"
}

@Composable
fun UserManagementPage(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var activeTab by remember { mutableStateOf("users") }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.lg)
        }
    }) {
        // Tab bar
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.sm)
                property("border-bottom", "2px solid ${colors.border}")
                paddingBottom(Spacing.sm)
            }
        }) {
            listOf("users" to "Users", "permissions" to "Permissions").forEach { (id, label) ->
                Button({
                    onClick { activeTab = id }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        border(0.px)
                        cursor("pointer")
                        fontSize(Typography.base)
                        fontWeight(if (activeTab == id) "600" else "400")
                        color(if (activeTab == id) Color(colors.primary) else Color(colors.textMuted))
                        backgroundColor(Color.transparent)
                        property("border-bottom", if (activeTab == id) "2px solid ${colors.primary}" else "none")
                        marginBottom((-2).px)
                    }
                }) { Text(label) }
            }
        }

        when (activeTab) {
            "users" -> UsersTab(apiClient)
            "permissions" -> PermissionsTab(apiClient)
        }
    }
}

@Composable
private fun UsersTab(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var users by remember { mutableStateOf<List<AdminUserInfoDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var showInviteDialog by remember { mutableStateOf(false) }
    var toastMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            users = apiClient.listAdminUsers()
            loading = false
        } catch (e: Exception) {
            error = e.message
            loading = false
        }
    }

    LaunchedEffect(toastMessage) {
        if (toastMessage != null) {
            kotlinx.coroutines.delay(3000)
            toastMessage = null
        }
    }

    fun reload() {
        scope.launch {
            loading = true
            try {
                users = apiClient.listAdminUsers()
                error = null
            } catch (e: Exception) {
                error = e.message
            }
            loading = false
        }
    }

    // Toast
    toastMessage?.let { msg ->
        Div({
            style {
                position(Position.Fixed)
                property("bottom", "24px")
                property("right", "24px")
                padding(Spacing.md, Spacing.lg)
                backgroundColor(Color("#10b981"))
                color(Color.white)
                borderRadius(BorderRadius.md)
                property("z-index", "1000")
                fontSize(Typography.sm)
            }
        }) { Text(msg) }
    }

    // Header with invite button
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
        }
    }) {
        H3({ style { margin(0.px) } }) { Text("Users") }
        Button({
            onClick { showInviteDialog = true }
            style {
                padding(Spacing.sm, Spacing.lg)
                backgroundColor(Color(colors.primary))
                color(Color.white)
                border(0.px)
                borderRadius(BorderRadius.md)
                cursor("pointer")
                fontSize(Typography.sm)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.xs)
            }
        }) {
            IconPlus(size = 16)
            Text("Invite User")
        }
    }

    if (loading) {
        Div({ style { padding(Spacing.xl); textAlign("center") } }) { Text("Loading users...") }
    } else if (error != null) {
        Div({ style { padding(Spacing.lg); color(Color("#ef4444")) } }) { Text("Error: $error") }
    } else {
        // Users table
        Div({
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                property("border", "1px solid ${colors.border}")
                overflow("hidden")
                themeTransition()
            }
        }) {
            // Table header
            Div({
                style {
                    display(DisplayStyle.Grid)
                    property("grid-template-columns", "2fr 1fr 1fr 1fr 150px")
                    padding(Spacing.md, Spacing.lg)
                    backgroundColor(Color(colors.surfaceHover))
                    fontWeight("600")
                    fontSize(Typography.sm)
                    color(Color(colors.textMuted))
                }
            }) {
                Span { Text("Email") }
                Span { Text("Role") }
                Span { Text("Status") }
                Span { Text("Enabled") }
                Span { Text("Actions") }
            }

            users.forEach { user ->
                UserRow(user, apiClient, colors, scope, onReload = { reload() }, onToast = { toastMessage = it })
            }

            if (users.isEmpty()) {
                Div({
                    style {
                        padding(Spacing.xl)
                        textAlign("center")
                        color(Color(colors.textMuted))
                    }
                }) { Text("No users found") }
            }
        }
    }

    // Invite dialog
    if (showInviteDialog) {
        InviteUserDialog(
            apiClient = apiClient,
            onClose = { showInviteDialog = false },
            onCreated = {
                showInviteDialog = false
                toastMessage = "User invited successfully"
                reload()
            }
        )
    }
}

@Composable
private fun UserRow(
    user: AdminUserInfoDto,
    apiClient: ApiClient,
    colors: com.castly.admin.ui.theme.AppColors,
    scope: kotlinx.coroutines.CoroutineScope,
    onReload: () -> Unit,
    onToast: (String) -> Unit
) {
    var changing by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "2fr 1fr 1fr 1fr 150px")
            padding(Spacing.md, Spacing.lg)
            property("border-top", "1px solid ${colors.border}")
            alignItems(AlignItems.Center)
            fontSize(Typography.sm)
        }
    }) {
        // Email
        Span({ style { property("word-break", "break-all") } }) { Text(user.email) }

        // Role dropdown
        Select({
            onChange { event ->
                val newGroup = event.value ?: ""
                changing = true
                scope.launch {
                    try {
                        apiClient.setUserGroup(user.email, newGroup)
                        onToast("Role updated for ${user.email}")
                        onReload()
                    } catch (e: Exception) {
                        onToast("Error: ${e.message}")
                    }
                    changing = false
                }
            }
            if (changing) attr("disabled", "")
            style {
                padding(4.px, 8.px)
                borderRadius(BorderRadius.sm)
                property("border", "1px solid ${colors.border}")
                backgroundColor(Color(colors.surface))
                color(Color(colors.text))
                fontSize(Typography.sm)
                cursor("pointer")
            }
        }) {
            GROUPS.forEach { (value, label) ->
                val castlyGroup = user.groups.firstOrNull { it.startsWith("castly-") } ?: ""
                Option(value, { if (castlyGroup == value) selected() }) { Text(label) }
            }
        }

        // Status
        Span({
            style {
                color(Color(if (user.status == "CONFIRMED") "#10b981" else "#f59e0b"))
            }
        }) { Text(user.status) }

        // Enabled
        Span({
            style {
                color(Color(if (user.enabled) "#10b981" else "#ef4444"))
            }
        }) { Text(if (user.enabled) "Active" else "Disabled") }

        // Actions
        Div({ style { display(DisplayStyle.Flex); gap(Spacing.xs) } }) {
            Button({
                onClick {
                    scope.launch {
                        try {
                            if (user.enabled) {
                                apiClient.disableUser(user.email)
                                onToast("User disabled: ${user.email}")
                            } else {
                                apiClient.enableUser(user.email)
                                onToast("User enabled: ${user.email}")
                            }
                            onReload()
                        } catch (e: Exception) {
                            onToast("Error: ${e.message}")
                        }
                    }
                }
                style {
                    padding(4.px, 8.px)
                    border(0.px)
                    borderRadius(BorderRadius.sm)
                    cursor("pointer")
                    fontSize(Typography.xs)
                    backgroundColor(Color(if (user.enabled) "#fef2f2" else "#f0fdf4"))
                    color(Color(if (user.enabled) "#ef4444" else "#10b981"))
                }
            }) {
                Text(if (user.enabled) "Disable" else "Enable")
            }
        }
    }
}

@Composable
private fun InviteUserDialog(
    apiClient: ApiClient,
    onClose: () -> Unit,
    onCreated: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var email by remember { mutableStateOf("") }
    var group by remember { mutableStateOf("castly-viewer") }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }

    // Overlay
    Div({
        onClick { onClose() }
        style {
            position(Position.Fixed)
            property("top", "0")
            property("left", "0")
            property("right", "0")
            property("bottom", "0")
            backgroundColor(Color("rgba(0,0,0,0.5)"))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "999")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                padding(Spacing.xl)
                minWidth(400.px)
                maxWidth(500.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(Spacing.lg)
            }
        }) {
            H3({ style { margin(0.px) } }) { Text("Invite User") }

            error?.let {
                Div({ style { color(Color("#ef4444")); fontSize(Typography.sm) } }) { Text(it) }
            }

            Div({
                style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(Spacing.sm) }
            }) {
                Label { Text("Email") }
                Input(InputType.Email) {
                    value(email)
                    onInput { email = it.value }
                    attr("placeholder", "user@example.com")
                    style {
                        padding(Spacing.sm, Spacing.md)
                        borderRadius(BorderRadius.md)
                        property("border", "1px solid ${colors.border}")
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        fontSize(Typography.base)
                        width(100.percent)
                    }
                }
            }

            Div({
                style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(Spacing.sm) }
            }) {
                Label { Text("Role") }
                Select({
                    onChange { group = it.value ?: "castly-viewer" }
                    style {
                        padding(Spacing.sm, Spacing.md)
                        borderRadius(BorderRadius.md)
                        property("border", "1px solid ${colors.border}")
                        backgroundColor(Color(colors.surface))
                        color(Color(colors.text))
                        fontSize(Typography.base)
                        width(100.percent)
                    }
                }) {
                    GROUPS.filter { it.first.isNotBlank() }.forEach { (value, label) ->
                        Option(value, { if (group == value) selected() }) { Text(label) }
                    }
                }
            }

            Div({
                style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(Spacing.sm) }
            }) {
                Button({
                    onClick { onClose() }
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        backgroundColor(Color(colors.surfaceHover))
                        color(Color(colors.text))
                    }
                }) { Text("Cancel") }

                Button({
                    onClick {
                        if (email.isBlank()) {
                            error = "Email is required"
                            return@onClick
                        }
                        loading = true
                        error = null
                        scope.launch {
                            try {
                                apiClient.createAdminUser(email, group)
                                onCreated()
                            } catch (e: Exception) {
                                error = e.message
                            }
                            loading = false
                        }
                    }
                    if (loading) attr("disabled", "")
                    style {
                        padding(Spacing.sm, Spacing.lg)
                        border(0.px)
                        borderRadius(BorderRadius.md)
                        cursor("pointer")
                        backgroundColor(Color(colors.primary))
                        color(Color.white)
                        property("opacity", if (loading) "0.7" else "1")
                    }
                }) { Text(if (loading) "Inviting..." else "Invite") }
            }
        }
    }
}

// ── Permissions Tab ────────────────────────────────────────────────

@Composable
private fun PermissionsTab(apiClient: ApiClient) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()

    var permissions by remember { mutableStateOf<List<PermissionEntryDto>>(emptyList()) }
    var verticals by remember { mutableStateOf<List<String>>(emptyList()) }
    var groups by remember { mutableStateOf<List<String>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var toastMessage by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        try {
            val matrix = apiClient.getPermissionMatrix()
            permissions = matrix.permissions
            verticals = matrix.verticals
            groups = matrix.groups
            loading = false
        } catch (e: Exception) {
            error = e.message
            loading = false
        }
    }

    LaunchedEffect(toastMessage) {
        if (toastMessage != null) {
            kotlinx.coroutines.delay(3000)
            toastMessage = null
        }
    }

    // Toast
    toastMessage?.let { msg ->
        Div({
            style {
                position(Position.Fixed)
                property("bottom", "24px")
                property("right", "24px")
                padding(Spacing.md, Spacing.lg)
                backgroundColor(Color("#10b981"))
                color(Color.white)
                borderRadius(BorderRadius.md)
                property("z-index", "1000")
                fontSize(Typography.sm)
            }
        }) { Text(msg) }
    }

    H3({ style { margin(0.px, 0.px, Spacing.md) } }) { Text("Permission Matrix") }
    P({
        style { color(Color(colors.textMuted)); fontSize(Typography.sm); marginTop(0.px) }
    }) {
        Text("Configure what each role can do per feature area. Levels: NONE < VIEW < EDIT < PUBLISH < DELETE")
    }

    if (loading) {
        Div({ style { padding(Spacing.xl); textAlign("center") } }) { Text("Loading permissions...") }
    } else if (error != null) {
        Div({ style { padding(Spacing.lg); color(Color("#ef4444")) } }) { Text("Error: $error") }
    } else {
        Div({
            style {
                overflow("auto")
                backgroundColor(Color(colors.surface))
                borderRadius(BorderRadius.lg)
                property("border", "1px solid ${colors.border}")
                themeTransition()
            }
        }) {
            Table({
                style {
                    width(100.percent)
                    property("border-collapse", "collapse")
                    fontSize(Typography.sm)
                }
            }) {
                // Header row
                Thead {
                    Tr({
                        style {
                            backgroundColor(Color(colors.surfaceHover))
                        }
                    }) {
                        Th({
                            style {
                                padding(Spacing.md)
                                textAlign("left")
                                fontWeight("600")
                                color(Color(colors.textMuted))
                                property("border-bottom", "1px solid ${colors.border}")
                            }
                        }) { Text("Group") }

                        verticals.forEach { vertical ->
                            Th({
                                style {
                                    padding(Spacing.md)
                                    textAlign("center")
                                    fontWeight("600")
                                    color(Color(colors.textMuted))
                                    property("border-bottom", "1px solid ${colors.border}")
                                    minWidth(100.px)
                                }
                            }) { Text(VERTICAL_LABELS[vertical] ?: vertical) }
                        }
                    }
                }

                // Data rows
                Tbody {
                    groups.forEach { group ->
                        Tr({
                            style {
                                property("border-bottom", "1px solid ${colors.border}")
                            }
                        }) {
                            Td({
                                style {
                                    padding(Spacing.md)
                                    fontWeight("500")
                                    property("white-space", "nowrap")
                                }
                            }) {
                                val displayName = GROUPS.find { it.first == group }?.second ?: group
                                Text(displayName)
                            }

                            verticals.forEach { vertical ->
                                val currentLevel = permissions
                                    .find { it.groupName == group && it.vertical == vertical }
                                    ?.permissionLevel ?: "NONE"

                                Td({
                                    style {
                                        padding(4.px)
                                        textAlign("center")
                                    }
                                }) {
                                    Select({
                                        onChange { event ->
                                            val newLevel = event.value ?: return@onChange
                                            scope.launch {
                                                try {
                                                    apiClient.updatePermission(group, vertical, newLevel)
                                                    // Update local state
                                                    permissions = permissions.map { entry ->
                                                        if (entry.groupName == group && entry.vertical == vertical) {
                                                            entry.copy(permissionLevel = newLevel)
                                                        } else entry
                                                    }
                                                    toastMessage = "Updated $group / $vertical -> $newLevel"
                                                } catch (e: Exception) {
                                                    toastMessage = "Error: ${e.message}"
                                                }
                                            }
                                        }
                                        style {
                                            padding(4.px, 6.px)
                                            borderRadius(BorderRadius.sm)
                                            property("border", "1px solid ${colors.border}")
                                            backgroundColor(Color(colors.surface))
                                            color(Color(levelColor(currentLevel)))
                                            fontWeight("600")
                                            fontSize(Typography.xs)
                                            cursor("pointer")
                                            width(90.px)
                                        }
                                    }) {
                                        PERMISSION_LEVELS.forEach { level ->
                                            Option(level, { if (currentLevel == level) selected() }) {
                                                Text(level)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
